import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { animateScroll } from "react-scroll";
import { Menu, X } from "lucide-react";
import {
  splitLocalePath,
  useLocalePath,
  useTranslation,
} from "@thyra/landing-i18n";
import { ThemeLogo } from "../ThemeLogo";

export type NavItem = { name: string; link: string };

type NavbarProps = {
  children: ReactNode;
  className?: string;
};

type NavBodyProps = {
  children: ReactNode;
  className?: string;
  visible?: boolean;
  /** When true, navbar uses compact width (resizable effect on scroll). */
  scrolled?: boolean;
};

type NavItemsProps = {
  items: NavItem[];
  className?: string;
  onItemClick?: (e: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => void;
};

type NavbarButtonProps = {
  href?: string;
  as?: "a" | "button";
  children: ReactNode;
  className?: string;
  variant?: "primary" | "secondary" | "dark" | "gradient";
  onClick?: () => void;
};

type MobileNavProps = {
  children: ReactNode;
  className?: string;
  visible?: boolean;
};

type MobileNavHeaderProps = {
  children: ReactNode;
  className?: string;
};

type MobileNavToggleProps = {
  isOpen: boolean;
  onClick: () => void;
};

type MobileNavMenuProps = {
  children: ReactNode;
  className?: string;
  isOpen: boolean;
};

export function Navbar({ children, className = "" }: NavbarProps) {
  return (
    <div className={`relative w-full ${className}`}>
      <nav className="fixed left-0 right-0 top-0 z-50 flex w-full items-center justify-center px-4 pt-2">
        {children}
      </nav>
    </div>
  );
}

export function NavBody({
  children,
  className = "",
  visible = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  scrolled: _ = false,
}: NavBodyProps) {
  if (!visible) return null;
  return (
    <div
      className={`relative hidden lg:flex items-center justify-center gap-4 py-3 px-5 rounded-full w-full transition-all duration-300 ease-out backdrop-blur-md border border-border bg-background/95 shadow-sm max-w-5xl ${className}`}
    >
      {children}
    </div>
  );
}

export function NavItems({
  items,
  className = "",
  onItemClick,
}: NavItemsProps) {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {items.map((item, idx) => (
        <a
          key={`nav-${idx}`}
          href={item.link}
          onClick={(e) => onItemClick?.(e, item)}
          className="relative whitespace-nowrap px-3 py-2 text-sm font-medium text-foreground/90 hover:text-foreground transition-colors rounded-full hover:bg-muted/80"
        >
          {item.name}
        </a>
      ))}
    </div>
  );
}

export function NavbarLogo() {
  const location = useLocation();
  const { t } = useTranslation("common");
  const lp = useLocalePath();
  const isHome = splitLocalePath(location.pathname).path === "/";

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isHome) {
      e.preventDefault();
      animateScroll.scrollToTop({ smooth: true, duration: 500 });
    }
  };

  return (
    <Link
      to={lp("/")}
      onClick={handleClick}
      className="flex shrink-0 items-center gap-2 text-foreground transition-transform duration-200 hover:scale-105 active:scale-95"
      aria-label={t("nav.home")}
    >
      <ThemeLogo height={28} className="h-7 w-auto" />
    </Link>
  );
}

const variantStyles = {
  primary: "bg-primary text-white hover:opacity-95 shadow-sm",
  secondary: "bg-muted text-foreground hover:bg-muted/80 border border-border",
  dark: "bg-foreground text-white hover:opacity-95",
  gradient:
    "bg-gradient-to-r from-primary to-primary/80 text-white hover:opacity-95 shadow-sm",
};

export function NavbarButton({
  href,
  as: As = "a",
  children,
  className = "",
  variant = "primary",
  onClick,
}: NavbarButtonProps) {
  const base =
    "inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition-opacity " +
    variantStyles[variant];
  const combined = `${base} ${className}`.trim();

  if (As === "button") {
    return (
      <button type="button" onClick={onClick} className={combined}>
        {children}
      </button>
    );
  }
  return (
    <a href={href ?? "#"} onClick={onClick} className={combined}>
      {children}
    </a>
  );
}

export function MobileNav({
  children,
  className = "",
  visible = true,
}: MobileNavProps) {
  if (!visible) return null;
  return (
    <div
      className={`flex flex-col lg:hidden w-full max-w-[calc(100vw-2rem)] mx-auto rounded-2xl overflow-hidden bg-background/95 backdrop-blur-md border border-border shadow-sm ${className}`}
      style={{ padding: "12px 20px" }}
    >
      {children}
    </div>
  );
}

export function MobileNavHeader({
  children,
  className = "",
}: MobileNavHeaderProps) {
  return (
    <div
      className={`flex flex-row items-center justify-between w-full ${className}`}
    >
      {children}
    </div>
  );
}

export function MobileNavToggle({ isOpen, onClick }: MobileNavToggleProps) {
  const { t } = useTranslation("common");
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-2 text-foreground/90 hover:text-foreground rounded-lg"
      aria-label={isOpen ? t("nav.closeMenu") : t("nav.openMenu")}
      aria-expanded={isOpen}
    >
      {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
    </button>
  );
}

export function MobileNavMenu({
  children,
  className = "",
  isOpen,
}: MobileNavMenuProps) {
  const { t } = useTranslation("common");
  if (!isOpen) return null;
  return (
    <div
      className={`flex flex-col gap-4 pt-4 pb-2 border-t border-border mt-2 ${className}`}
      role="dialog"
      aria-label={t("nav.mobileMenu")}
    >
      {children}
    </div>
  );
}
